import React from "react";
import { Routes, Route, Navigate } from "react-router-dom";
import { PageContainer, PageErrorBoundary } from "@custodio-ai/components";
import { SignIn } from "./pages/SignIn";
import { SignUp } from "./pages/SignUp";
import { authClient } from "./auth-client";

// Protected Route Component
const ProtectedRoute: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const { data: session, isPending } = authClient.useSession();

  if (isPending) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-secondary-600">Loading...</div>
      </div>
    );
  }

  if (!session) {
    return <Navigate to="/signin" replace />;
  }

  return <>{children}</>;
};

// Guest Route Component — redirects authenticated users away from auth pages
const GuestRoute: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { data: session, isPending } = authClient.useSession();

  if (isPending) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-secondary-600">Loading...</div>
      </div>
    );
  }

  if (session) {
    return <Navigate to="/" replace />;
  }

  return <>{children}</>;
};

const Dashboard = React.lazy(() =>
  import("./pages/Dashboard").then((module) => ({ default: module.Dashboard })),
);
const DocumentsPage = React.lazy(() =>
  import("./pages/DocumentsPage").then((module) => ({
    default: module.DocumentsPage,
  })),
);
const DocumentPage = React.lazy(() =>
  import("./pages/DocumentPage").then((module) => ({
    default: module.DocumentPage,
  })),
);
const OCRViewer = React.lazy(() =>
  import("./components/document/OCRViewer").then((module) => ({
    default: module.OCRViewer,
  })),
);
const Profile = React.lazy(() =>
  import("./pages/Profile").then((module) => ({ default: module.Profile })),
);
const AccountSettings = React.lazy(() =>
  import("./pages/AccountSettings").then((module) => ({
    default: module.AccountSettings,
  })),
);
const Pricing = React.lazy(() =>
  import("./pages/Pricing").then((module) => ({ default: module.Pricing })),
);
const ProductsPage = React.lazy(() =>
  import("./pages/ProductsPage").then((module) => ({
    default: module.ProductsPage,
  })),
);
const ProductDetail = React.lazy(() =>
  import("./pages/ProductDetail").then((module) => ({
    default: module.ProductDetail,
  })),
);
const AddProduct = React.lazy(() =>
  import("./pages/AddProduct").then((module) => ({
    default: module.AddProduct,
  })),
);
const EditProduct = React.lazy(() =>
  import("./pages/EditProduct").then((module) => ({
    default: module.EditProduct,
  })),
);
const CompanyProductCatalog = React.lazy(() =>
  import("./pages/CompanyProductCatalog").then((module) => ({
    default: module.CompanyProductCatalog,
  })),
);
const AddCompanyProduct = React.lazy(() =>
  import("./pages/AddCompanyProduct").then((module) => ({
    default: module.AddCompanyProduct,
  })),
);
const EditCompanyProduct = React.lazy(() =>
  import("./pages/EditCompanyProduct").then((module) => ({
    default: module.EditCompanyProduct,
  })),
);
const Companies = React.lazy(() =>
  import("./pages/Companies").then((module) => ({ default: module.Companies })),
);
const ProductValidation = React.lazy(() =>
  import("./pages/ProductValidation").then((module) => ({
    default: module.ProductValidation,
  })),
);
const FilePreview = React.lazy(() =>
  import("./components/document/FilePreview").then((module) => ({
    default: module.FilePreview,
  })),
);
const BillAnalysisViewer = React.lazy(() =>
  import("./components/document/BillAnalysisViewer").then((module) => ({
    default: module.BillAnalysisViewer,
  })),
);
const ProductsTab = React.lazy(() =>
  import("./components/document/ProductsTab").then((module) => ({
    default: module.ProductsTab,
  })),
);
const ActivitiesPage = React.lazy(() =>
  import("./pages/ActivitiesPage").then((module) => ({
    default: module.ActivitiesPage,
  })),
);

export const AppRoutes: React.FC = () => {
  return (
    <Routes>
      {/* Guest-only routes — redirect authenticated users to dashboard */}
      <Route path="/signin" element={<GuestRoute><SignIn /></GuestRoute>} />
      <Route path="/signup" element={<GuestRoute><SignUp /></GuestRoute>} />

      {/* Protected routes */}
      <Route
        path="/"
        element={
          <ProtectedRoute>
            <PageContainer>
              <PageErrorBoundary>
                <Dashboard />
              </PageErrorBoundary>
            </PageContainer>
          </ProtectedRoute>
        }
      />
      <Route
        path="/documents"
        element={
          <ProtectedRoute>
            <PageContainer>
              <PageErrorBoundary>
                <DocumentsPage />
              </PageErrorBoundary>
            </PageContainer>
          </ProtectedRoute>
        }
      />
      <Route
        path="/activities"
        element={
          <ProtectedRoute>
            <PageContainer>
              <PageErrorBoundary>
                <ActivitiesPage />
              </PageErrorBoundary>
            </PageContainer>
          </ProtectedRoute>
        }
      />
      <Route
        path="/documents/:fileId"
        element={
          <ProtectedRoute>
            <DocumentPage />
          </ProtectedRoute>
        }
      >
        <Route index element={<FilePreview />} />
        <Route path="ocr" element={<OCRViewer />} />
        <Route path="analysis" element={<BillAnalysisViewer />} />
        <Route path="products" element={<ProductsTab />} />
      </Route>
      <Route
        path="/products"
        element={
          <ProtectedRoute>
            <PageContainer>
              <PageErrorBoundary>
                <ProductsPage />
              </PageErrorBoundary>
            </PageContainer>
          </ProtectedRoute>
        }
      />
      <Route
        path="/products/add"
        element={
          <ProtectedRoute>
            <PageContainer>
              <PageErrorBoundary>
                <AddProduct />
              </PageErrorBoundary>
            </PageContainer>
          </ProtectedRoute>
        }
      />
      <Route
        path="/products/:productId"
        element={
          <ProtectedRoute>
            <PageContainer>
              <PageErrorBoundary>
                <ProductDetail />
              </PageErrorBoundary>
            </PageContainer>
          </ProtectedRoute>
        }
      />
      <Route
        path="/products/:productId/edit"
        element={
          <ProtectedRoute>
            <PageContainer>
              <PageErrorBoundary>
                <EditProduct />
              </PageErrorBoundary>
            </PageContainer>
          </ProtectedRoute>
        }
      />
      <Route
        path="/profile"
        element={
          <ProtectedRoute>
            <PageContainer>
              <PageErrorBoundary>
                <Profile />
              </PageErrorBoundary>
            </PageContainer>
          </ProtectedRoute>
        }
      />
      <Route
        path="/settings"
        element={
          <ProtectedRoute>
            <PageContainer>
              <PageErrorBoundary>
                <AccountSettings />
              </PageErrorBoundary>
            </PageContainer>
          </ProtectedRoute>
        }
      />
      <Route
        path="/pricing"
        element={
          <ProtectedRoute>
            <PageContainer>
              <PageErrorBoundary>
                <Pricing />
              </PageErrorBoundary>
            </PageContainer>
          </ProtectedRoute>
        }
      />
      <Route
        path="/product-catalog"
        element={
          <ProtectedRoute>
            <PageContainer>
              <PageErrorBoundary>
                <CompanyProductCatalog />
              </PageErrorBoundary>
            </PageContainer>
          </ProtectedRoute>
        }
      />
      <Route
        path="/product-catalog/add"
        element={
          <ProtectedRoute>
            <PageContainer>
              <PageErrorBoundary>
                <AddCompanyProduct />
              </PageErrorBoundary>
            </PageContainer>
          </ProtectedRoute>
        }
      />
      <Route
        path="/product-catalog/:productId"
        element={
          <ProtectedRoute>
            <PageContainer>
              <PageErrorBoundary>
                <EditCompanyProduct />
              </PageErrorBoundary>
            </PageContainer>
          </ProtectedRoute>
        }
      />
      <Route
        path="/product-catalog/:productId/edit"
        element={
          <ProtectedRoute>
            <PageContainer>
              <PageErrorBoundary>
                <EditCompanyProduct />
              </PageErrorBoundary>
            </PageContainer>
          </ProtectedRoute>
        }
      />
      <Route
        path="/product-catalog/:serviceId"
        element={
          <ProtectedRoute>
            <PageContainer>
              <PageErrorBoundary>
                <EditCompanyProduct />
              </PageErrorBoundary>
            </PageContainer>
          </ProtectedRoute>
        }
      />
      <Route
        path="/product-catalog/:serviceId/edit"
        element={
          <ProtectedRoute>
            <PageContainer>
              <PageErrorBoundary>
                <EditCompanyProduct />
              </PageErrorBoundary>
            </PageContainer>
          </ProtectedRoute>
        }
      />
      <Route
        path="/companies"
        element={
          <ProtectedRoute>
            <PageContainer>
              <PageErrorBoundary>
                <Companies />
              </PageErrorBoundary>
            </PageContainer>
          </ProtectedRoute>
        }
      />
      <Route
        path="/validate-product"
        element={
          <ProtectedRoute>
            <PageContainer>
              <PageErrorBoundary>
                <ProductValidation />
              </PageErrorBoundary>
            </PageContainer>
          </ProtectedRoute>
        }
      />
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
};
